import React from "react";
import { FatButton, FatButtonInputRightSideProp } from "../__Internal__";
import { useHTMLInputElementValidationProjection } from "../hooks/useHTMLInputElementValidationProjection";
import { PropError, PropLabel } from "../PropX";
import { useCheckboxGroupState } from "./CheckboxStateContext";
import { LooseCheckboxButton } from "./LooseCheckboxButton";

type Props = {
  /**
   * The name of the checkbox field.
   */
  name: string;
  /**
   * The value of the checkbox field. Necessary, but kind of useless for controlled form inputs.
   */
  value: string;

  /**
   * The title of the checkbox.
   */
  title: React.ReactNode;

  /**
   * The subtitle of the checkbox.
   * @default undefined
   */
  subtitle?: React.ReactNode;

  /**
   * The right side of the checkbox.
   * @default undefined
   */
  rightSide?: FatButtonInputRightSideProp;

  /**
   * An optional extension that appears under the checkbox when selected.
   * @default null
   */
  extension?: React.ReactNode;

  /**
   * An optional icon that appears to the right of the checkbox.
   * @default undefined
   */
  icon?: string;

  /**
   * Whether the checkbox is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the checkbox is required.
   * @default false
   */
  required?: boolean;

  /**
   * Whether the checkbox is checked.
   * @default false
   */
  checked?: boolean;

  /**
   * Whether the checkbox is indeterminate.
   * @default false
   */
  indeterminate?: boolean;

  /**
   * A label to display above the input.
   * @default undefined
   */
  label?: PropLabel;

  /**
   * When the checkbox changes from checked to unchecked or vice versa.
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;

  /**
   * A custom error to display for the input. Overrides standard errors created by `required`.
   * @default undefined
   */
  error?: PropError | PropError[];

  /**
   * The event for which to validate the input.
   * @default "manual"
   */
  validateOn?: "change" | "blur" | "manual";

  /**
   * Whether the checkbox button is compact or has min-width.
   * @default false
   */
  compact?: boolean;

  /**
   * Testing utility tag.
   * @default "checkbox-button"
   */
  "data-testid"?: string;
};

export const CheckboxButton: React.FC<Props> = (props) => {
  const { validateOn = "manual" } = props;
  const ref = React.useRef<HTMLInputElement>(null);
  const [errors, propsForInput] = useHTMLInputElementValidationProjection(props, { ref, validateOn });
  const { horizontal } = useCheckboxGroupState();
  const id = `${props.name}-${props.value}`;

  return (
    <FatButton.BaseContainer $horizontal={horizontal}>
      {props.label &&
        PropLabel.renderForBeforeInput(props.label, { htmlFor: id, required: props.required, invalid: propsForInput.invalid })}
      <LooseCheckboxButton ref={ref} id={id} {...props} {...propsForInput} horizontal={horizontal} />
      {PropError.renderGroupAfterInput(errors)}
    </FatButton.BaseContainer>
  );
};
